/**
 * "Liquid glass" class recipes (iOS 26 look): a translucent fill with a
 * vertical sheen, a bright rim on the top edge, a faint one at the bottom and a
 * soft drop shadow.
 *
 * Only controls floating over the page background get a real backdrop blur.
 * Inside cards the backdrop is already soft, so the cheaper faux glass (no
 * backdrop-filter) looks the same without re-blurring on every aurora frame.
 *
 * Never put GLASS_CONTROL on an element framer-motion animates (opacity or
 * transform): Chromium mis-renders backdrop-filter there and the element shows
 * up smeared. Animate a wrapper and keep the glass element inside it static.
 *
 * Class names are spelled out in full so Tailwind's content scan finds them.
 */

/** Frosted glass for controls over the page background (header, hero, clock). */
export const GLASS_CONTROL = [
  'tw-border tw-border-white/70 tw-bg-white/50 tw-backdrop-blur-xl tw-backdrop-saturate-150',
  'tw-bg-[linear-gradient(180deg,rgba(255,255,255,0.8)_0%,rgba(255,255,255,0.2)_50%,rgba(255,255,255,0.08)_100%)]',
  'tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(255,255,255,0.4),0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_-10px_rgba(15,23,42,0.25)]',
  'dark:tw-border-white/[0.14] dark:tw-bg-white/[0.06]',
  'dark:tw-bg-[linear-gradient(180deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0.04)_50%,rgba(255,255,255,0.02)_100%)]',
  'dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_0_rgba(255,255,255,0.05),0_10px_30px_-12px_rgba(0,0,0,0.8)]',
].join(' ');

/** Faux glass for chips and small controls sitting on a card. */
export const GLASS_CHIP = [
  'tw-border tw-border-white tw-bg-white/60',
  'tw-bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(255,255,255,0.45))]',
  'tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_0_0_1px_rgba(15,23,42,0.07),0_2px_6px_-2px_rgba(15,23,42,0.12)]',
  'dark:tw-border-white/10 dark:tw-bg-white/[0.05]',
  'dark:tw-bg-[linear-gradient(180deg,rgba(255,255,255,0.1),rgba(255,255,255,0.02))]',
  'dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_2px_6px_-2px_rgba(0,0,0,0.6)]',
].join(' ');

/**
 * Hover/press feedback for clickable glass. Not for stretched-link buttons:
 * a transform on them would re-anchor their ::after overlay mid-click.
 */
export const GLASS_PRESSABLE = [
  'tw-transition-[background-color,color,box-shadow,transform] tw-duration-200',
  'hover:tw-bg-white/75 dark:hover:tw-bg-white/[0.11]',
  'active:tw-scale-[0.96]',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
].join(' ');

/**
 * Large frosted surface for app chrome (sidebar, top bar, dropdowns). More
 * opaque than GLASS_CONTROL because page content scrolls underneath it. The
 * dark fill sits just above the legacy pages' #1a1c23 background.
 */
export const GLASS_PANEL = [
  'tw-border tw-border-white/70 tw-bg-white/75 tw-backdrop-blur-2xl tw-backdrop-saturate-150',
  'tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(15,23,42,0.05),0_18px_40px_-20px_rgba(15,23,42,0.3)]',
  'dark:tw-border-white/10 dark:tw-bg-[#22262f]/80',
  'dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_20px_44px_-20px_rgba(0,0,0,0.85)]',
].join(' ');

/**
 * Dropdown/popover surface. Nearly opaque: it fades in on an animated wrapper,
 * and while an ancestor's opacity is below 1 the backdrop blur has nothing to
 * sample. Don't nest it inside an element that has a backdrop-filter itself -
 * that ancestor would limit the blur to its own box.
 */
export const GLASS_POPOVER = [
  'tw-border tw-border-white/80 tw-bg-white/90 tw-backdrop-blur-2xl tw-backdrop-saturate-150',
  'tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_24px_48px_-16px_rgba(15,23,42,0.3)]',
  'dark:tw-border-white/10 dark:tw-bg-[#262a34]/95',
  'dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_24px_48px_-16px_rgba(0,0,0,0.85)]',
].join(' ');

/**
 * Round glass icon button for use on a glass bar (no backdrop blur of its own).
 * Text colour comes from the caller: two colour utilities on one element would
 * resolve by Tailwind's stylesheet order, not by class order.
 */
export const ICON_BUTTON = [
  'tw-relative tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full',
  GLASS_CHIP,
  GLASS_PRESSABLE,
].join(' ');

/**
 * Text input / select on a glass chip. Legacy dark-mode CSS forces input
 * borders; the explicit border colours here (via GLASS_CHIP) take precedence.
 * 16px text on phones: iOS Safari zooms into any smaller field on focus.
 */
export const GLASS_FIELD = [
  'tw-h-10 tw-w-full tw-rounded-full tw-px-4 tw-text-base tw-text-slate-800 tw-shadow-none tw-outline-none sm:tw-text-sm',
  'placeholder:tw-text-slate-400 focus:tw-ring-2 focus:tw-ring-teal-500/40',
  'dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500',
  GLASS_CHIP,
].join(' ');

/** Count bubble pinned to the top-right corner of an ICON_BUTTON. */
export const COUNT_BADGE = [
  'tw-pointer-events-none tw-absolute -tw-right-1 -tw-top-1 tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center',
  'tw-rounded-full tw-bg-rose-500 tw-px-1 tw-text-[10px] tw-font-bold tw-leading-none tw-text-white',
  'tw-shadow-sm tw-ring-2 tw-ring-white dark:tw-ring-[#22262f]',
].join(' ');
